<script setup lang="ts">
import { ButtonVariant, OrButtonV3 as OrButton } from '@onereach/ui-components';
import { AccountData } from '@or-sdk/accounts';
import { onMounted, onUnmounted } from 'vue';

import { useAuthStore } from '@/stores/auth/auth';
import { isElectronEnvironment, isIOSWebView } from '@/utils/environmnentCheckers';

import config from '../../config';
import { useErrorHandler } from '../../utils/error';
const {
  showError,
} = useErrorHandler();

const props = withDefaults(defineProps<{
  verificationCode?: string;
  label?: string;
  modelValue?: string;
  showIcon?: boolean;
  buttonType?: `${ButtonVariant}`;
  keepLoggedIn: boolean;
  accountData: AccountData;
  disabled?: boolean;
  fromExpired?: boolean;
}>(), {
  modelValue: '',
  label: '',
  showIcon: true,
  buttonType: ButtonVariant.Outlined,
  verificationCode: '',
  keepLoggedIn: false,
  disabled: false,
  fromExpired: false,
});

const googleLoading = defineModel<boolean>('googleLoading', { default: false });

const emit = defineEmits(['update:modelValue']);

const authStore = useAuthStore();

async function initCallback({ credential }: { credential: string; }) {
  googleLoading.value = true;
  emit('update:modelValue', credential);
  try {
    await authStore.signIn({
      googleCode: credential,
      keepLoggedIn: props.keepLoggedIn,
      accountData: props.accountData,
    });
  } catch (e) {
    showError(e as string);
  }
  googleLoading.value = false;
}

function googleFallback() {
  let url = `${config.GLOBAL_SSO_URL}?GATEWAY_URL=${config.GATEWAY_URL}&AUTH_UI_URL=${config.AUTH_UI_URL}&PROVIDERS_ACCOUNT_ID=${config.PROVIDERS_ACCOUNT_ID}&SDK_API_URL=${config.SDK_API_URL}`;
  if (props.verificationCode) url += `&MFACode=${props.verificationCode}`;

  if (isIOSWebView() || isElectronEnvironment()) {
    localStorage.setItem('keepLoggedIn', `${props.keepLoggedIn}`);
    return window.location.href = url;
  }

  const left = Math.max(window.innerWidth / 2 - 300, 0);
  const top = Math.max(window.innerHeight / 2 - 300, 0);

  return window.open(url, 'SSO', `width=590,height=600,left=${left},top=${top}`);
}

function promptCallback(notification) {
  if (notification.l === 'unknown_reason' && notification.g === 'skipped') {
    googleFallback();
  }
  else if (notification.isNotDisplayed && notification.isNotDisplayed()) {
    googleFallback();
  }
}

const openSSOIframe = () => {
  // web view not supports google fedcm api, but old code not working too
  if (/onereach/i.test(navigator.userAgent) || isIOSWebView() || isElectronEnvironment()) { // tmp disabled
    googleFallback();
    return;
  }
  googleLoading.value = true;
  if (window.self !== window.top && !props.fromExpired) // in iframe
    return window.parent.postMessage({ type: 'GOOGLE_LOGIN_PROMPT_OPEN' }, '*');
  // @ts-expect-error its imported in index.html
  // eslint-disable-next-line
  google.accounts.id.initialize({
    client_id: config.GOOGLE_APP_ID,
    callback: initCallback,
  });
  try {
    // @ts-expect-error its imported in index.html
    // eslint-disable-next-line
    google.accounts.id.prompt(promptCallback);
  } catch (e) {
    showError(e as string);
    googleLoading.value = false;
  }
};

function handleWindowMessage(event) {
  switch (event.data.type) {
    case 'GOOGLE_INIT_CALLBACK':
      return initCallback(event.data.data);
    case 'GOOGLE_PROMPT_CALLBACK':
      return promptCallback(event.data.data);
  }
}

onMounted(() => {
  window.addEventListener('message', handleWindowMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleWindowMessage);
  googleLoading.value = false;
});
</script>

<template>
  <div class="w-full">
    <OrButton
      color="primary"
      class="w-full"
      :variant="buttonType"
      :loading="googleLoading"
      :disabled="disabled"
      @click="openSSOIframe"
    >
      <svg
        v-if="showIcon"
        viewBox="0 0 24 24"
        width="16"
        height="16"
      >
        <g
          xmlns="http://www.w3.org/2000/svg"
          transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)"
        >
          <path
            fill="#4285F4"
            d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"
          />
          <path
            fill="#34A853"
            d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"
          />
          <path
            fill="#FBBC05"
            d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"
          />
          <path
            fill="#EA4335"
            d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"
          />
        </g>
      </svg>
      &nbsp;
      {{ label || $t('login.password_restore.Sign in') }}
    </OrButton>
  </div>
</template>
